import React, { useEffect, useRef, useState } from 'react';
import type { Scene, SceneSeat } from '@/data/scenes';
import { formatFocus } from '@/hooks/usePresence';

// The glow behind someone reaches full warmth after this long focused
const FULL_GLOW_MINUTES = 120;

interface SeatLoopProps {
  seat: SceneSeat;
  visible: boolean;
  paused?: boolean;
  focusMinutes?: number;
  where?: string | null; // e.g. "3am in Seoul"
  who?: string; // label prefix, e.g. "you"
}

// One seat's character loop, laid exactly over its region of the plate. A soft
// silhouette mask (or, without one, a feathered edge) reveals only the character.
// Behind it, a warm glow grows with how long they've been focused; hovering or
// tapping the seat shows the time.
const SeatLoop: React.FC<SeatLoopProps> = ({
  seat,
  visible,
  paused = false,
  focusMinutes,
  where,
  who,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [tapped, setTapped] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (visible && !paused) video.play().catch(() => {});
    else video.pause();
  }, [visible, paused]);

  useEffect(() => {
    if (!tapped) return;
    const timer = setTimeout(() => setTapped(false), 3000);
    return () => clearTimeout(timer);
  }, [tapped]);

  const box = {
    left: `${seat.x * 100}%`,
    top: `${seat.y * 100}%`,
    width: `${seat.w * 100}%`,
    height: `${seat.h * 100}%`,
  };
  const hasFocus = visible && focusMinutes !== undefined;
  const still = seat.still;
  const warmth = hasFocus ? Math.min(1, focusMinutes / FULL_GLOW_MINUTES) : 0;

  return (
    <>
      <div
        className={`scene-glow ${hasFocus ? 'is-visible' : ''}`}
        style={{ ...box, '--warmth': warmth } as React.CSSProperties}
        aria-hidden="true"
      />
      {still && (
        <img
          className={`scene-seat scene-seat-still ${visible ? 'is-visible' : ''}`}
          style={{
            left: `${still.x * 100}%`,
            top: `${still.y * 100}%`,
            width: `${still.w * 100}%`,
            height: `${still.h * 100}%`,
          }}
          src={still.src}
          alt=""
          aria-hidden="true"
        />
      )}
      <video
        ref={videoRef}
        className={`scene-seat ${seat.mask ? 'has-mask' : ''} ${visible ? 'is-visible' : ''}`}
        style={{
          ...box,
          ...(seat.mask && {
            WebkitMaskImage: `url(${seat.mask})`,
            maskImage: `url(${seat.mask})`,
          }),
        }}
        src={seat.video}
        poster={seat.poster}
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
      />
      {hasFocus && (
        <button
          type="button"
          className={`scene-seat-hit ${tapped ? 'is-tapped' : ''}`}
          style={box}
          onClick={() => setTapped(true)}
          aria-label={`${who ?? 'someone'} focusing for ${formatFocus(focusMinutes)}${where ? `, ${where}` : ''}`}
        >
          <span className="scene-seat-label">
            {who && `${who} · `}focusing {formatFocus(focusMinutes)}
            {where && ` · ${where}`}
          </span>
        </button>
      )}
    </>
  );
};

interface SceneRendererProps {
  scene: Scene;
  occupiedSeats: Set<number>; // indexes into scene.seats
  seatFocus?: Map<number, number>; // seat index -> minutes focused
  seatWhere?: Map<number, string | null>; // seat index -> "3am in Seoul"
  showHero: boolean;
  heroPaused?: boolean;
  heroFocus?: number;
  lookId?: string;
}

const SceneRenderer: React.FC<SceneRendererProps> = ({
  scene,
  occupiedSeats,
  seatFocus,
  seatWhere,
  showHero,
  heroPaused = false,
  heroFocus,
  lookId,
}) => {
  const hero = scene.looks?.find((l) => l.look === lookId) ?? scene.hero;

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Sized to cover the viewport at the plate's aspect, so seat boxes line up */}
      <div className="scene-frame" style={{ '--scene-aspect': scene.aspect } as React.CSSProperties}>
        <video
          className="scene-plate"
          src={scene.plate.video}
          poster={scene.plate.image}
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        />
        {scene.seats.map((seat, i) => (
          <SeatLoop
            key={seat.id}
            seat={seat}
            visible={occupiedSeats.has(i)}
            focusMinutes={seatFocus?.get(i)}
            where={seatWhere?.get(i)}
          />
        ))}
        {hero && (
          <SeatLoop
            key={hero.id}
            seat={hero}
            visible={showHero}
            paused={heroPaused}
            focusMinutes={heroFocus}
            who="you"
          />
        )}
      </div>

      {/* Same readability gradient as the default session video */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.75) 100%)',
        }}
      />
    </div>
  );
};

export default SceneRenderer;
